import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, User, Shield, Link, CheckCircle, Settings2, Database, Globe, Mic, Mail, ExternalLink, Loader2, X, Building2, CalendarDays, Crown, Video, Eye, EyeOff, Search, Linkedin, Twitter, Facebook, Instagram, Briefcase, Phone, MapPin, FileText, MessageCircle, Sparkles, Camera, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import VoiceCaptureTab from '../components/VoiceCaptureTab';
import OrganizationSettings from '../components/OrganizationSettings';
import AIVisualSettings from '../components/ai-entity/AIVisualSettings';
import { usePlan } from '../context/PlanContext';
import UpgradeModal from '../components/UpgradeModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Settings() {
    const { user, getAuthHeader, logout } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState('profile');
    const [profile, setProfile] = useState({
        name: user?.name || '', picture: user?.picture || '', picture_s3_key: '',
        title: '', phone: '', timezone: '', bio: '', pronouns: '',
        linkedin_url: '', twitter_url: '', facebook_url: '', instagram_url: '', personal_website: '',
        is_independent_consultant: false,
        company_name: '', company_street: '', company_city: '', company_state: '',
        company_zip: '', company_country: '', company_website: '', company_logo_url: '', company_logo_s3_key: '', company_abn: '',
    });
    const [profileLoaded, setProfileLoaded] = useState(false);
    const [saving, setSaving] = useState(false);
    const [csvFile, setCsvFile] = useState(null);
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);
    
    // Gmail state
    const [gmailStatus, setGmailStatus] = useState(null);
    const [gmailLoading, setGmailLoading] = useState(false);
    const [connectingGmail, setConnectingGmail] = useState(false);

    const [outlookStatus, setOutlookStatus] = useState(null);
    const [outlookLoading, setOutlookLoading] = useState(false);
    const [connectingOutlook, setConnectingOutlook] = useState(false);

    // Calendar state
    const [calendarStatus, setCalendarStatus] = useState(null);
    const [calendarLoading, setCalendarLoading] = useState(false);
    const [connectingCalendar, setConnectingCalendar] = useState(false);

    // Zoom state
    const [zoomStatus, setZoomStatus] = useState(null);
    const [zoomLoading, setZoomLoading] = useState(false);

    // Apify state
    const [apifyKey, setApifyKey] = useState('');
    const [apifyStatus, setApifyStatus] = useState(null);
    const [apifyValidating, setApifyValidating] = useState(false);
    const [showApifyKey, setShowApifyKey] = useState(false);

    // WhatsApp state
    const [whatsappStatus, setWhatsappStatus] = useState(null);
    const [whatsappLoading, setWhatsappLoading] = useState(false);
    const [connectingWhatsApp, setConnectingWhatsApp] = useState(false);

    // Exa state
    const [exaKey, setExaKey] = useState('');
    const [exaStatus, setExaStatus] = useState(null);
    const [exaValidating, setExaValidating] = useState(false);
    const [showExaKey, setShowExaKey] = useState(false);

    // Check for Gmail callback params
    useEffect(() => {
        if (searchParams.get('gmail_connected') === 'true') {
            toast.success('Gmail connected successfully!');
            setActiveTab('integrations');
            loadGmailStatus();
            window.history.replaceState({}, '', '/settings');
        }
        if (searchParams.get('gmail_error')) {
            toast.error(`Gmail connection failed: ${searchParams.get('gmail_error')}`);
            setActiveTab('integrations');
            window.history.replaceState({}, '', '/settings');
        }
        if (searchParams.get('outlook_connected') === 'true') {
            toast.success('Outlook connected successfully!');
            setActiveTab('integrations');
            loadOutlookStatus();
            window.history.replaceState({}, '', '/settings');
        }
        if (searchParams.get('outlook_error')) {
            toast.error(`Outlook connection failed: ${searchParams.get('outlook_error')}`);
            setActiveTab('integrations');
            window.history.replaceState({}, '', '/settings');
        }
        if (searchParams.get('calendar_connected') === 'true') {
            toast.success('Google Calendar connected successfully!');
            setActiveTab('integrations');
            loadCalendarStatus();
            window.history.replaceState({}, '', '/settings');
        }
        if (searchParams.get('calendar_error')) {
            toast.error(`Calendar connection failed: ${searchParams.get('calendar_error')}`);
            setActiveTab('integrations');
            window.history.replaceState({}, '', '/settings');
        }
        if (searchParams.get('zoom_connected') === 'true') {
            toast.success('Zoom connected successfully!');
            setActiveTab('integrations');
            loadZoomStatus();
            window.history.replaceState({}, '', '/settings');
        }
        if (searchParams.get('zoom_error')) {
            toast.error(`Zoom connection failed: ${searchParams.get('zoom_error')}`);
            setActiveTab('integrations');
            window.history.replaceState({}, '', '/settings');
        }
    }, [searchParams]);

    // Load profile + integration statuses on mount
    useEffect(() => {
        // Fetch full profile data
        axios.get(`${API}/users/profile`, { headers: getAuthHeader() })
            .then(res => {
                if (res.data) {
                    setProfile(prev => ({ ...prev, ...res.data }));
                    setProfileLoaded(true);
                }
            })
            .catch(() => setProfileLoaded(true));
        loadGmailStatus();
        loadOutlookStatus();
        loadCalendarStatus();
        loadZoomStatus();
        loadWhatsAppStatus();
        loadApifyStatus();
        loadExaStatus();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const loadGmailStatus = async () => {
        setGmailLoading(true);
        try {
            const res = await axios.get(`${API}/oauth/gmail/status`, { headers: getAuthHeader() });
            setGmailStatus(res.data);
        } catch (err) {
            console.error('Failed to load Gmail status:', err);
        } finally {
            setGmailLoading(false);
        }
    };


    const loadOutlookStatus = async () => {
        setOutlookLoading(true);
        try {
            const res = await axios.get(`${API}/oauth/outlook/status`, { headers: getAuthHeader() });
            setOutlookStatus(res.data);
        } catch (err) {
            console.error('Failed to load Outlook status:', err);
        } finally {
            setOutlookLoading(false);
        }
    };

    const connectGmail = async () => {
        setConnectingGmail(true);
        try {
            const res = await axios.get(`${API}/oauth/gmail/login?user_id=${user.user_id}`, { headers: getAuthHeader() });
            if (res.data.auth_url) {
                window.location.href = res.data.auth_url;
            }
        } catch (err) {
            toast.error('Failed to start Gmail connection');
            setConnectingGmail(false);
        }
    };


    const connectOutlook = async () => {
        setConnectingOutlook(true);
        try {
            const res = await axios.get(`${API}/oauth/outlook/login?user_id=${user.user_id}`, { headers: getAuthHeader() });
            if (res.data.auth_url) {
                window.location.href = res.data.auth_url;
            }
        } catch (err) {
            toast.error('Failed to start Outlook connection');
            setConnectingOutlook(false);
        }
    };

    const disconnectGmail = async () => {
        try {
            await axios.post(`${API}/oauth/gmail/disconnect`, {}, { headers: getAuthHeader() });
            setGmailStatus({ connected: false });
            toast.success('Gmail disconnected');
        } catch {
            toast.error('Failed to disconnect Gmail');
        }
    };


    const disconnectOutlook = async () => {
        try {
            await axios.post(`${API}/oauth/outlook/disconnect`, {}, { headers: getAuthHeader() });
            setOutlookStatus({ connected: false });
            toast.success('Outlook disconnected');
        } catch {
            toast.error('Failed to disconnect Outlook');
        }
    };

    // ── WhatsApp functions ─────────────────────────────────────────────────
    const loadWhatsAppStatus = async () => {
        setWhatsappLoading(true);
        try {
            const res = await axios.get(`${API}/whatsapp/status`, { headers: getAuthHeader() });
            setWhatsappStatus(res.data);
        } catch (err) {
            console.error('Failed to load WhatsApp status:', err);
        } finally {
            setWhatsappLoading(false);
        }
    };

    const connectWhatsApp = async () => {
        setConnectingWhatsApp(true);
        try {
            // Load Facebook SDK dynamically
            if (!window.FB) {
                await new Promise((resolve, reject) => {
                    const script = document.createElement('script');
                    script.src = 'https://connect.facebook.net/en_US/sdk.js';
                    script.async = true;
                    script.defer = true;
                    script.onload = resolve;
                    script.onerror = reject;
                    document.body.appendChild(script);
                });
                window.fbAsyncInit = () => {
                    window.FB.init({
                        appId: process.env.REACT_APP_META_APP_ID,
                        autoLogAppEvents: true,
                        xfbml: false,
                        version: 'v21.0',
                    });
                };
                window.fbAsyncInit();
            }

            // Launch Embedded Signup
            window.FB.login((response) => {
                if (response.authResponse) {
                    const code = response.authResponse.code;
                    // Complete signup on backend
                    _completeWhatsAppSignup(code);
                } else {
                    toast.error('WhatsApp connection cancelled');
                    setConnectingWhatsApp(false);
                }
            }, {
                config_id: process.env.REACT_APP_META_CONFIG_ID, // Embedded Signup config
                response_type: 'code',
                override_default_response_type: true,
                extras: {
                    setup: {}, // WhatsApp Embedded Signup
                    featureType: '',
                    sessionInfoVersion: 2,
                },
            });
        } catch (err) {
            toast.error('Failed to start WhatsApp connection');
            setConnectingWhatsApp(false);
        }
    };

    const _completeWhatsAppSignup = async (code) => {
        try {
            // The Embedded Signup callback provides code; we also need waba_id and phone_number_id
            // These come from the sessionInfoListener message event
            const res = await axios.post(`${API}/whatsapp/connect`, {
                code,
                waba_id: window._whatsappWabaId || '',
                phone_number_id: window._whatsappPhoneNumberId || '',
                display_name: window._whatsappDisplayName || '',
                phone_number: window._whatsappPhoneNumber || '',
            }, { headers: getAuthHeader() });
            toast.success(`WhatsApp connected: ${res.data.display_name || 'Success'}`);
            loadWhatsAppStatus();
        } catch (err) {
            const detail = err.response?.data?.detail || 'Failed to complete WhatsApp setup';
            toast.error(typeof detail === 'string' ? detail : detail.message || 'Failed');
        } finally {
            setConnectingWhatsApp(false);
        }
    };

    // Listen for Embedded Signup session info (WABA ID, phone number ID)
    useEffect(() => {
        const handler = (event) => {
            if (event.data?.type === 'WA_EMBEDDED_SIGNUP') {
                const data = event.data.data;
                if (data) {
                    window._whatsappWabaId = data.waba_id || '';
                    window._whatsappPhoneNumberId = data.phone_number_id || '';
                    window._whatsappDisplayName = data.display_name || '';
                    window._whatsappPhoneNumber = data.phone_number || '';
                }
            }
        };
        window.addEventListener('message', handler);
        return () => window.removeEventListener('message', handler);
    }, []);

    const disconnectWhatsApp = async () => {
        try {
            await axios.post(`${API}/whatsapp/disconnect`, {
                account_id: whatsappStatus?.account_id,
            }, { headers: getAuthHeader() });
            setWhatsappStatus({ connected: false });
            toast.success('WhatsApp disconnected');
        } catch {
            toast.error('Failed to disconnect WhatsApp');
        }
    };

    const loadCalendarStatus = async () => {
        setCalendarLoading(true);
        try {
            const res = await axios.get(`${API}/oauth/calendar/status`, { headers: getAuthHeader() });
            setCalendarStatus(res.data);
        } catch (err) {
            console.error('Failed to load Calendar status:', err);
        } finally {
            setCalendarLoading(false);
        }
    };

    const connectCalendar = async () => {
        setConnectingCalendar(true);
        try {
            const res = await axios.get(`${API}/oauth/calendar/login?user_id=${user.user_id}`, { headers: getAuthHeader() });
            if (res.data.auth_url) {
                window.location.href = res.data.auth_url;
            }
        } catch (err) {
            toast.error('Failed to start Calendar connection');
            setConnectingCalendar(false);
        }
    };

    const disconnectCalendar = async () => {
        try {
            await axios.post(`${API}/oauth/calendar/disconnect`, {}, { headers: getAuthHeader() });
            setCalendarStatus({ connected: false });
            toast.success('Calendar disconnected');
        } catch {
            toast.error('Failed to disconnect Calendar');
        }
    };

    const loadZoomStatus = async () => {
        try {
            const res = await axios.get(`${API}/oauth/zoom/status`, { headers: getAuthHeader() });
            setZoomStatus(res.data);
        } catch {}
    };

    const connectZoom = () => {
        window.location.href = `${API}/oauth/zoom/login?user_id=${user?.user_id}`;
    };

    const disconnectZoom = async () => {
        setZoomLoading(true);
        try {
            await axios.post(`${API}/oauth/zoom/disconnect`, {}, { headers: getAuthHeader() });
            setZoomStatus(null);
            toast.success('Zoom disconnected');
        } catch { toast.error('Failed to disconnect'); }
        finally { setZoomLoading(false); }
    };

    const loadApifyStatus = async () => {
        try {
            const res = await axios.get(`${API}/organizations/current`, { headers: getAuthHeader() });
            const key = res.data?.settings?.apify_api_key;
            if (key) {
                setApifyKey(key);
                try {
                    const valRes = await axios.post(`${API}/enrichment/apify/validate-key`, { api_key: key }, { headers: getAuthHeader() });
                    setApifyStatus(valRes.data);
                } catch {}
            }
        } catch {}
    };

    const validateApifyKey = async () => {
        if (!apifyKey.trim()) return toast.error('Enter an API key');
        setApifyValidating(true);
        try {
            const res = await axios.post(`${API}/enrichment/apify/validate-key`, { api_key: apifyKey }, { headers: getAuthHeader() });
            if (res.data.valid) {
                await axios.put(`${API}/organizations/current`, { settings: { apify_api_key: apifyKey } }, { headers: getAuthHeader() });
                setApifyStatus(res.data);
                toast.success('Apify connected!');
            } else {
                toast.error('Invalid API key');
                setApifyStatus(null);
            }
        } catch (err) { toast.error(err.response?.data?.detail || `Validation failed: ${err.message}`); }
        finally { setApifyValidating(false); }
    };

    const disconnectApify = async () => {
        try {
            await axios.put(`${API}/organizations/current`, { settings: { apify_api_key: null } }, { headers: getAuthHeader() });
            setApifyKey('');
            setApifyStatus(null);
            toast.success('Apify disconnected');
        } catch { toast.error('Failed to disconnect Apify'); }
    };

    const loadExaStatus = async () => {
        try {
            const res = await axios.get(`${API}/organizations/current`, { headers: getAuthHeader() });
            const key = res.data?.settings?.exa_api_key;
            if (key) {
                setExaKey(key);
                try {
                    const valRes = await axios.post(`${API}/enrichment/exa/validate-key`, { api_key: key }, { headers: getAuthHeader() });
                    if (valRes.data.valid) setExaStatus(valRes.data);
                } catch {}
            }
        } catch {}
    };

    const validateExaKey = async () => {
        if (!exaKey.trim()) return toast.error('Enter an API key');
        setExaValidating(true);
        try {
            const res = await axios.post(`${API}/enrichment/exa/validate-key`, { api_key: exaKey }, { headers: getAuthHeader() });
            if (res.data.valid) {
                await axios.put(`${API}/organizations/current`, { settings: { exa_api_key: exaKey } }, { headers: getAuthHeader() });
                setExaStatus(res.data);
                toast.success('Exa connected!');
            } else {
                toast.error('Invalid API key');
                setExaStatus(null);
            }
        } catch (err) { toast.error(err.response?.data?.detail || 'Validation failed'); }
        finally { setExaValidating(false); }
    };

    const disconnectExa = async () => {
        try {
            await axios.put(`${API}/organizations/current`, { settings: { exa_api_key: null } }, { headers: getAuthHeader() });
            setExaKey('');
            setExaStatus(null);
            toast.success('Exa disconnected');
        } catch { toast.error('Failed to disconnect'); }
    };

    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const profilePhotoRef = React.useRef(null);
    const companyLogoRef = React.useRef(null);

    const handleImageUpload = async (file, field, s3KeyField, setUploading) => {
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            toast.error('Please select an image file');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            toast.error('Image must be under 5MB');
            return;
        }
        setUploading(true);
        try {
            // Get presigned upload URL
            const urlRes = await axios.post(`${API}/users/profile/upload-url`, {
                file_name: file.name,
                mime_type: file.type,
            }, { headers: getAuthHeader() });
            const { upload_url, s3_key } = urlRes.data;

            // Upload directly to S3
            await fetch(upload_url, {
                method: 'PUT',
                body: file,
                headers: { 'Content-Type': file.type },
            });

            // Save s3_key to profile — backend will generate download URL on next fetch
            await axios.put(`${API}/users/profile`, {
                [s3KeyField]: s3_key,
            }, { headers: getAuthHeader() });

            // Show local preview immediately
            const localUrl = URL.createObjectURL(file);
            setProfile(p => ({ ...p, [field]: localUrl, [s3KeyField]: s3_key }));
            toast.success('Photo uploaded');
        } catch (err) {
            console.error('Upload failed:', err);
            toast.error('Failed to upload photo');
        } finally {
            setUploading(false);
        }
    };

    const removeImage = async (field, s3KeyField) => {
        try {
            await axios.put(`${API}/users/profile`, {
                [field]: '', [s3KeyField]: '',
            }, { headers: getAuthHeader() });
            setProfile(p => ({ ...p, [field]: '', [s3KeyField]: '' }));
            toast.success('Photo removed');
        } catch {
            toast.error('Failed to remove photo');
        }
    };

    const saveProfile = async () => {
        setSaving(true);
        try {
            // Strip `picture` / `company_logo_url` from the PUT — those are
            // managed server-side via `picture_s3_key` / `company_logo_s3_key`.
            // The local-preview path uses URL.createObjectURL which produces a
            // `blob:` URL that's only valid in the current tab; sending it
            // would overwrite the real presigned URL in the DB and break the
            // Header avatar + email-signature thumbnail for every other
            // surface. See v2.16.34 fix.
            const { picture, company_logo_url, ...safeProfile } = profile;
            await axios.put(`${API}/users/profile`, safeProfile, { headers: getAuthHeader() });
            toast.success('Profile updated');
        } catch { toast.error('Failed to update profile'); }
        finally { setSaving(false); }
    };

    const handleCSVImport = async () => {
        if (!csvFile) return toast.error('Please select a CSV file');
        setImporting(true);
        try {
            const formData = new FormData();
            formData.append('file', csvFile);
            const res = await axios.post(`${API}/integrations/pitchbook/import`, formData, {
                headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
            });
            setImportResult(res.data);
            toast.success(`Imported ${res.data.imported_accounts} accounts, ${res.data.imported_contacts} contacts`);
        } catch { toast.error('Import failed'); }
        finally { setImporting(false); }
    };

    const tabs = [
        { key: 'profile', label: 'Profile', icon: User },
        { key: 'organization', label: 'Organization', icon: Building2 },
        { key: 'plan', label: 'Plan & Usage', icon: Crown },
        { key: 'voice', label: 'Voice Capture', icon: Mic },
        { key: 'integrations', label: 'Integrations', icon: Link },
        { key: 'ai-visual', label: 'AI Assistant', icon: Sparkles },
        { key: 'security', label: 'Security', icon: Shield },
    ];

    return (
        <div className="space-y-6">
            <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                <div className="border-b border-stone-200 px-4 overflow-x-auto">
                    <div className="flex gap-0 min-w-max">
                        {tabs.map(tab => {
                            const Icon = tab.icon;
                            return (
                                <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                                    className={`flex items-center gap-2 px-4 py-3.5 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.key ? 'border-stone-700 text-stone-700' : 'border-transparent text-stone-500 hover:text-stone-700'}`}>
                                    <Icon className="w-4 h-4" />{tab.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="p-3 sm:p-6">
                    {/* Profile */}
                    {activeTab === 'profile' && (
                        <div className="max-w-2xl space-y-6">
                            {/* Section 1: Basic Info */}
                            <div className="border border-stone-200 rounded-lg p-5">
                                <h3 className="text-sm font-semibold text-stone-900 mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    <User className="w-4 h-4 text-stone-500" /> Basic Information
                                </h3>
                                <div className="flex items-center gap-4 mb-5">
                                    <div className="relative group flex-shrink-0">
                                        <div className="w-16 h-16 rounded-full overflow-hidden bg-stone-100">
                                            {profile.picture ? <img src={profile.picture} alt={profile.name} className="w-full h-full object-cover" /> :
                                                <div className="w-full h-full flex items-center justify-center text-stone-700 font-bold text-xl">{profile.name?.charAt(0)}</div>}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => profilePhotoRef.current?.click()}
                                            disabled={uploadingPhoto}
                                            className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/40 flex items-center justify-center transition-colors cursor-pointer"
                                        >
                                            {uploadingPhoto
                                                ? <Loader2 className="w-5 h-5 text-white animate-spin" />
                                                : <Camera className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />}
                                        </button>
                                        <input ref={profilePhotoRef} type="file" accept="image/*" className="hidden"
                                            onChange={e => { handleImageUpload(e.target.files[0], 'picture', 'picture_s3_key', setUploadingPhoto); e.target.value = ''; }} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium text-stone-900">{user?.name}</p>
                                        <p className="text-sm text-stone-500">{user?.email}</p>
                                        <p className="text-xs text-stone-400 mt-0.5 capitalize">{user?.role?.replace('_', ' ')}</p>
                                        <div className="flex items-center gap-2 mt-1">
                                            <button type="button" onClick={() => profilePhotoRef.current?.click()} disabled={uploadingPhoto}
                                                className="text-xs text-stone-500 hover:text-stone-700 font-medium">
                                                {uploadingPhoto ? 'Uploading...' : 'Change Photo'}
                                            </button>
                                            {(profile.picture || profile.picture_s3_key) && (
                                                <button type="button" onClick={() => removeImage('picture', 'picture_s3_key')}
                                                    className="text-xs text-red-400 hover:text-red-600 font-medium flex items-center gap-0.5">
                                                    <Trash2 className="w-3 h-3" /> Remove
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Display Name</label>
                                        <input value={profile.name || ''} onChange={e => setProfile(p => ({ ...p, name: e.target.value }))}
                                            data-testid="profile-name-input"
                                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Position Title</label>
                                        <input value={profile.title || ''} onChange={e => setProfile(p => ({ ...p, title: e.target.value }))}
                                            placeholder="e.g. Senior Account Executive"
                                            data-testid="profile-title-input"
                                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Email</label>
                                        <input value={user?.email || ''} disabled
                                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-stone-50 text-stone-500 cursor-not-allowed" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Phone</label>
                                        <input value={profile.phone || ''} onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))}
                                            placeholder="+1 (555) 000-0000"
                                            data-testid="profile-phone-input"
                                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Pronouns</label>
                                        <input value={profile.pronouns || ''} onChange={e => setProfile(p => ({ ...p, pronouns: e.target.value }))}
                                            placeholder="e.g. he/him, she/her, they/them"
                                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Timezone</label>
                                        <select value={profile.timezone || ''} onChange={e => setProfile(p => ({ ...p, timezone: e.target.value }))}
                                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20">
                                            <option value="">Select timezone...</option>
                                            {(() => {
                                                try { return Intl.supportedValuesOf('timeZone').map(tz => <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>); }
                                                catch { return ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Anchorage', 'Pacific/Honolulu', 'Europe/London', 'Europe/Paris', 'Asia/Tokyo', 'Asia/Singapore', 'Australia/Sydney', 'UTC'].map(tz => <option key={tz} value={tz}>{tz}</option>); }
                                            })()}
                                        </select>
                                    </div>
                                </div>
                                {!profile.picture_s3_key && (
                                    <div className="mt-4">
                                        <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Profile Picture URL</label>
                                        <input value={profile.picture || ''} onChange={e => setProfile(p => ({ ...p, picture: e.target.value }))}
                                            placeholder="https://... or use the upload button above"
                                            data-testid="profile-picture-input"
                                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                                        <p className="text-xs text-stone-400 mt-1">Or click your avatar above to upload a photo directly.</p>
                                    </div>
                                )}
                                <div className="mt-4">
                                    <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Bio / About Me</label>
                                    <textarea value={profile.bio || ''} onChange={e => setProfile(p => ({ ...p, bio: e.target.value }))}
                                        rows={3} placeholder="Tell people a bit about yourself..."
                                        data-testid="profile-bio-input"
                                        className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20 resize-none" />
                                </div>
                            </div>

                            {/* Section 2: Social Links */}
                            <div className="border border-stone-200 rounded-lg p-5">
                                <h3 className="text-sm font-semibold text-stone-900 mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    <Link className="w-4 h-4 text-stone-500" /> Social Links
                                </h3>
                                <div className="space-y-3">
                                    {[
                                        { key: 'linkedin_url', label: 'LinkedIn', icon: Linkedin, placeholder: 'https://linkedin.com/in/yourprofile' },
                                        { key: 'twitter_url', label: 'Twitter / X', icon: Twitter, placeholder: 'https://x.com/yourhandle' },
                                        { key: 'facebook_url', label: 'Facebook', icon: Facebook, placeholder: 'https://facebook.com/yourprofile' },
                                        { key: 'instagram_url', label: 'Instagram', icon: Instagram, placeholder: 'https://instagram.com/yourhandle' },
                                        { key: 'personal_website', label: 'Personal Website', icon: Globe, placeholder: 'https://yourwebsite.com' },
                                    ].map(social => {
                                        const SocialIcon = social.icon;
                                        return (
                                            <div key={social.key} className="flex items-center gap-2">
                                                <SocialIcon className="w-4 h-4 text-stone-400 flex-shrink-0" />
                                                <input value={profile[social.key] || ''} onChange={e => setProfile(p => ({ ...p, [social.key]: e.target.value }))}
                                                    placeholder={social.placeholder}
                                                    className="flex-1 px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Section 3: Independent Consultant */}
                            <div className="border border-stone-200 rounded-lg p-5">
                                <h3 className="text-sm font-semibold text-stone-900 mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    <Briefcase className="w-4 h-4 text-stone-500" /> Independent Consultant
                                </h3>
                                <label className="flex items-center gap-3 cursor-pointer mb-4">
                                    <input type="checkbox" checked={profile.is_independent_consultant || false}
                                        onChange={e => setProfile(p => ({ ...p, is_independent_consultant: e.target.checked }))}
                                        data-testid="consultant-checkbox"
                                        className="w-4 h-4 rounded border-stone-300 text-stone-900 focus:ring-stone-900/20" />
                                    <div>
                                        <span className="text-sm font-medium text-stone-700">I am an independent consultant / freelancer</span>
                                        <p className="text-xs text-stone-400">Enable this to add your company details for use in signatures and emails</p>
                                    </div>
                                </label>
                                {profile.is_independent_consultant && (
                                    <div className="space-y-4 pt-2 border-t border-stone-100">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                                            <div>
                                                <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Company Name</label>
                                                <input value={profile.company_name || ''} onChange={e => setProfile(p => ({ ...p, company_name: e.target.value }))}
                                                    placeholder="Your company name"
                                                    data-testid="company-name-input"
                                                    className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Company Website</label>
                                                <input value={profile.company_website || ''} onChange={e => setProfile(p => ({ ...p, company_website: e.target.value }))}
                                                    placeholder="https://yourcompany.com"
                                                    className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Company Logo</label>
                                                <div className="flex items-center gap-3">
                                                    {profile.company_logo_url ? (
                                                        <div className="w-10 h-10 rounded border border-stone-200 overflow-hidden bg-white flex-shrink-0">
                                                            <img src={profile.company_logo_url} alt="Logo" className="w-full h-full object-contain" />
                                                        </div>
                                                    ) : (
                                                        <div className="w-10 h-10 rounded border border-dashed border-stone-300 bg-stone-50 flex items-center justify-center flex-shrink-0">
                                                            <Building2 className="w-4 h-4 text-stone-300" />
                                                        </div>
                                                    )}
                                                    <div className="flex items-center gap-2">
                                                        <button type="button" onClick={() => companyLogoRef.current?.click()} disabled={uploadingLogo}
                                                            className="px-3 py-1.5 text-xs font-medium border border-stone-200 rounded-lg hover:bg-stone-50 text-stone-600 flex items-center gap-1.5">
                                                            {uploadingLogo ? <><Loader2 className="w-3 h-3 animate-spin" /> Uploading...</> : <><Upload className="w-3 h-3" /> Upload Logo</>}
                                                        </button>
                                                        {(profile.company_logo_url || profile.company_logo_s3_key) && (
                                                            <button type="button" onClick={() => removeImage('company_logo_url', 'company_logo_s3_key')}
                                                                className="text-xs text-red-400 hover:text-red-600 font-medium flex items-center gap-0.5">
                                                                <Trash2 className="w-3 h-3" /> Remove
                                                            </button>
                                                        )}
                                                    </div>
                                                    <input ref={companyLogoRef} type="file" accept="image/*" className="hidden"
                                                        onChange={e => { handleImageUpload(e.target.files[0], 'company_logo_url', 'company_logo_s3_key', setUploadingLogo); e.target.value = ''; }} />
                                                </div>
                                                {!profile.company_logo_s3_key && (
                                                    <input value={profile.company_logo_url || ''} onChange={e => setProfile(p => ({ ...p, company_logo_url: e.target.value }))}
                                                        placeholder="Or paste a URL: https://..."
                                                        className="w-full mt-2 px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                                                )}
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">ABN / Tax ID</label>
                                                <input value={profile.company_abn || ''} onChange={e => setProfile(p => ({ ...p, company_abn: e.target.value }))}
                                                    placeholder="Optional"
                                                    className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                                                <MapPin className="w-3 h-3" /> Company Address
                                            </label>
                                            <div className="space-y-3">
                                                <input value={profile.company_street || ''} onChange={e => setProfile(p => ({ ...p, company_street: e.target.value }))}
                                                    placeholder="Street address"
                                                    className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                                    <input value={profile.company_city || ''} onChange={e => setProfile(p => ({ ...p, company_city: e.target.value }))}
                                                        placeholder="City" className="px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                                                    <input value={profile.company_state || ''} onChange={e => setProfile(p => ({ ...p, company_state: e.target.value }))}
                                                        placeholder="State" className="px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                                                    <input value={profile.company_zip || ''} onChange={e => setProfile(p => ({ ...p, company_zip: e.target.value }))}
                                                        placeholder="ZIP" className="px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                                                    <input value={profile.company_country || ''} onChange={e => setProfile(p => ({ ...p, company_country: e.target.value }))}
                                                        placeholder="Country" className="px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Merge Variables Info */}
                            <div className="bg-stone-50 border border-stone-200 rounded-lg p-4">
                                <div className="flex items-start gap-2">
                                    <FileText className="w-4 h-4 text-stone-400 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <p className="text-xs font-medium text-stone-600">Your profile fields are available as merge variables</p>
                                        <p className="text-xs text-stone-400 mt-0.5">
                                            Use variables like <code className="bg-stone-200 px-1 rounded">{'{{SenderName}}'}</code>, <code className="bg-stone-200 px-1 rounded">{'{{SenderTitle}}'}</code>, <code className="bg-stone-200 px-1 rounded">{'{{SenderPhone}}'}</code>, <code className="bg-stone-200 px-1 rounded">{'{{SenderCompany}}'}</code> in email templates and signatures to auto-fill your details.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <button onClick={saveProfile} disabled={saving} data-testid="save-profile-btn"
                                className="px-5 py-2.5 bg-stone-900 text-white text-sm font-medium rounded-lg hover:bg-stone-800 disabled:opacity-50">
                                {saving ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    )}

                    {/* Organization */}
                    {activeTab === 'organization' && (
                        <OrganizationSettings />
                    )}

                    {/* Plan & Usage */}
                    {activeTab === 'plan' && <PlanUsageTab />}

                    {/* Integrations */}
                    {activeTab === 'integrations' && (
                        <div className="space-y-4 max-w-lg">
                            <p className="text-sm text-stone-500">Connect your email and calendar accounts to automatically log communications.</p>
                            
                            {/* Gmail Integration */}
                            <div className="p-4 rounded-xl border border-stone-200 hover:border-stone-300 transition-colors">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                                            <Mail className="w-5 h-5 text-red-500" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-stone-900">Gmail</p>
                                            <p className="text-xs text-stone-400">Sync emails and send from your Gmail</p>
                                        </div>
                                    </div>
                                    {gmailLoading ? (
                                        <Loader2 className="w-5 h-5 text-stone-400 animate-spin" />
                                    ) : gmailStatus?.connected ? (
                                        <span className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full">
                                            <CheckCircle className="w-3 h-3" /> Connected
                                        </span>
                                    ) : gmailStatus?.needs_reconnect ? (
                                        <button
                                            onClick={connectGmail}
                                            disabled={connectingGmail}
                                            className="px-3 py-1.5 text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors disabled:opacity-50">
                                            {connectingGmail ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Reconnect'}
                                        </button>
                                    ) : (
                                        <button
                                            onClick={connectGmail}
                                            disabled={connectingGmail}
                                            data-testid="settings-connect-gmail"
                                            className="px-3 py-1.5 text-xs font-medium bg-stone-100 text-stone-900 border border-stone-300 rounded-lg hover:bg-stone-100 transition-colors disabled:opacity-50">
                                            {connectingGmail ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Connect'}
                                        </button>
                                    )}
                                </div>
                                
                                {gmailStatus?.needs_reconnect && (
                                    <div className="mt-4 pt-4 border-t border-amber-100">
                                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                            <p className="text-xs font-medium text-amber-800">Session expired for {gmailStatus.gmail_email || gmailStatus.gmail_name || 'your Gmail'}</p>
                                            <p className="text-[11px] text-amber-600 mt-1">Click Reconnect above to re-authorize your Gmail access.</p>
                                        </div>
                                    </div>
                                )}
                                {gmailStatus?.connected && (
                                    <div className="mt-4 pt-4 border-t border-stone-100">
                                        <div className="flex items-center gap-3 mb-3">
                                            {gmailStatus.gmail_picture && (
                                                <img src={gmailStatus.gmail_picture} alt="" className="w-8 h-8 rounded-full" />
                                            )}
                                            <div>
                                                <p className="text-sm font-medium text-stone-900">{gmailStatus.gmail_name}</p>
                                                <p className="text-xs text-stone-500">{gmailStatus.gmail_email}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => navigate('/inbox?source=gmail')}
                                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-stone-700 bg-stone-100 rounded-lg hover:bg-stone-100">
                                                <ExternalLink className="w-3 h-3" /> View Emails
                                            </button>
                                            <button
                                                onClick={disconnectGmail}
                                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100">
                                                <X className="w-3 h-3" /> Disconnect
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Google Calendar Integration */}
                            <div className="p-4 rounded-xl border border-stone-200 hover:border-stone-300 transition-colors">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                                            <CalendarDays className="w-5 h-5 text-emerald-600" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-stone-900">Google Calendar</p>
                                            <p className="text-xs text-stone-400">Sync meetings and schedule from CRM</p>
                                        </div>
                                    </div>
                                    {calendarLoading ? (
                                        <Loader2 className="w-5 h-5 text-stone-400 animate-spin" />
                                    ) : calendarStatus?.connected ? (
                                        <span className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full">
                                            <CheckCircle className="w-3 h-3" /> Connected
                                        </span>
                                    ) : (
                                        <button
                                            onClick={connectCalendar}
                                            disabled={connectingCalendar}
                                            data-testid="settings-connect-calendar"
                                            className="px-3 py-1.5 text-xs font-medium bg-stone-100 text-stone-900 border border-stone-300 rounded-lg hover:bg-stone-100 transition-colors disabled:opacity-50">
                                            {connectingCalendar ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Connect'}
                                        </button>
                                    )}
                                </div>

                                {calendarStatus?.connected && (
                                    <div className="mt-4 pt-4 border-t border-stone-100">
                                        <div className="flex items-center gap-3 mb-3">
                                            {calendarStatus.calendar_picture && (
                                                <img src={calendarStatus.calendar_picture} alt="" className="w-8 h-8 rounded-full" />
                                            )}
                                            <div>
                                                <p className="text-sm font-medium text-stone-900">{calendarStatus.calendar_name}</p>
                                                <p className="text-xs text-stone-500">{calendarStatus.calendar_email}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => navigate('/inbox')}
                                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-stone-700 bg-stone-100 rounded-lg hover:bg-stone-200">
                                                <CalendarDays className="w-3 h-3" /> View Meetings
                                            </button>
                                            <button
                                                onClick={disconnectCalendar}
                                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100">
                                                <X className="w-3 h-3" /> Disconnect
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Zoom Integration */}
                            <div className="p-4 rounded-xl border border-stone-200 hover:border-stone-300 transition-colors">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                                            <Video className="w-5 h-5 text-blue-600" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-stone-900">Zoom</p>
                                            <p className="text-xs text-stone-400">Connect your Zoom account to create video meetings for bookings</p>
                                        </div>
                                    </div>
                                    {zoomLoading ? (
                                        <Loader2 className="w-5 h-5 text-stone-400 animate-spin" />
                                    ) : zoomStatus?.connected ? (
                                        <span className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full">
                                            <CheckCircle className="w-3 h-3" /> Connected
                                        </span>
                                    ) : (
                                        <button
                                            onClick={connectZoom}
                                            data-testid="settings-connect-zoom"
                                            className="px-3 py-1.5 text-xs font-medium bg-stone-100 text-stone-900 border border-stone-300 rounded-lg hover:bg-stone-100 transition-colors disabled:opacity-50">
                                            Connect
                                        </button>
                                    )}
                                </div>

                                {zoomStatus?.connected && (
                                    <div className="mt-4 pt-4 border-t border-stone-100">
                                        <div className="flex items-center gap-3 mb-3">
                                            <div>
                                                <p className="text-sm font-medium text-stone-900">{zoomStatus.zoom_name}</p>
                                                <p className="text-xs text-stone-500">{zoomStatus.zoom_email}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={disconnectZoom}
                                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100">
                                                <X className="w-3 h-3" /> Disconnect
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* WhatsApp Business Integration */}
                            <div className="p-4 rounded-xl border border-stone-200 hover:border-stone-300 transition-colors">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#E8FFF0' }}>
                                            <MessageCircle className="w-5 h-5" style={{ color: '#25D366' }} />
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-stone-900">WhatsApp Business</p>
                                            <p className="text-xs text-stone-400">Send and receive WhatsApp messages from CRM</p>
                                        </div>
                                    </div>
                                    {whatsappLoading ? (
                                        <Loader2 className="w-5 h-5 text-stone-400 animate-spin" />
                                    ) : whatsappStatus?.connected ? (
                                        <span className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full">
                                            <CheckCircle className="w-3 h-3" /> Connected
                                        </span>
                                    ) : (
                                        <button
                                            onClick={connectWhatsApp}
                                            disabled={connectingWhatsApp}
                                            data-testid="settings-connect-whatsapp"
                                            className="px-3 py-1.5 text-xs font-medium bg-stone-100 text-stone-900 border border-stone-300 rounded-lg hover:bg-stone-200 transition-colors disabled:opacity-50">
                                            {connectingWhatsApp ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Connect'}
                                        </button>
                                    )}
                                </div>

                                {whatsappStatus?.connected && (
                                    <div className="mt-4 pt-4 border-t border-stone-100">
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#E8FFF0' }}>
                                                <MessageCircle className="w-4 h-4" style={{ color: '#25D366' }} />
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-stone-900">{whatsappStatus.display_name}</p>
                                                <p className="text-xs text-stone-500">{whatsappStatus.phone_number}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 mb-3">
                                            <span className={`px-2 py-0.5 text-xs rounded-full ${
                                                whatsappStatus.quality_rating === 'GREEN' ? 'bg-emerald-50 text-emerald-700' :
                                                whatsappStatus.quality_rating === 'YELLOW' ? 'bg-amber-50 text-amber-700' :
                                                'bg-red-50 text-red-700'
                                            }`}>
                                                Quality: {whatsappStatus.quality_rating}
                                            </span>
                                            <span className="px-2 py-0.5 text-xs rounded-full bg-stone-100 text-stone-600">
                                                {whatsappStatus.messaging_tier}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => navigate('/inbox?tab=whatsapp')}
                                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-stone-700 bg-stone-100 rounded-lg hover:bg-stone-200">
                                                <ExternalLink className="w-3 h-3" /> Open Chat
                                            </button>
                                            <button
                                                onClick={disconnectWhatsApp}
                                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100">
                                                <X className="w-3 h-3" /> Disconnect
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Apify Data Enrichment */}
                            <div className="p-4 rounded-xl border border-stone-200 hover:border-stone-300 transition-colors">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                                            <Database className="w-5 h-5 text-amber-600" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-stone-900">Apify Data Enrichment</p>
                                            <p className="text-xs text-stone-400">Connect Apify for LinkedIn scraping and web enrichment</p>
                                        </div>
                                    </div>
                                    {apifyStatus?.valid ? (
                                        <span className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full">
                                            <CheckCircle className="w-3 h-3" /> Connected
                                        </span>
                                    ) : null}
                                </div>

                                {apifyStatus?.valid ? (
                                    <div className="mt-4 pt-4 border-t border-stone-100">
                                        <div className="flex items-center gap-3 mb-3">
                                            <div>
                                                <p className="text-sm font-medium text-stone-900">{apifyStatus.username || 'Apify User'}</p>
                                                <p className="text-xs text-stone-500">Plan: {apifyStatus.plan || 'Unknown'}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={disconnectApify}
                                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100">
                                                <X className="w-3 h-3" /> Disconnect
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="mt-4 pt-4 border-t border-stone-100 space-y-3">
                                        <div className="relative">
                                            <input
                                                type={showApifyKey ? 'text' : 'password'}
                                                value={apifyKey}
                                                onChange={e => setApifyKey(e.target.value)}
                                                placeholder="Enter your Apify API key"
                                                className="w-full px-3 py-2 pr-10 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowApifyKey(!showApifyKey)}
                                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600">
                                                {showApifyKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            </button>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <button
                                                onClick={validateApifyKey}
                                                disabled={apifyValidating}
                                                className="px-3 py-1.5 text-xs font-medium bg-stone-900 text-white rounded-lg hover:bg-stone-800 disabled:opacity-50">
                                                {apifyValidating ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : null}
                                                {apifyValidating ? 'Validating...' : 'Validate & Connect'}
                                            </button>
                                            <a href="https://apify.com" target="_blank" rel="noopener noreferrer"
                                                className="text-xs text-stone-400 hover:text-stone-600 flex items-center gap-1">
                                                Don't have an account? Sign up at apify.com <ExternalLink className="w-3 h-3" />
                                            </a>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Exa AI Search */}
                            <div className="p-4 rounded-xl border border-stone-200 hover:border-stone-300 transition-colors">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                                            <Search className="w-5 h-5 text-indigo-600" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-stone-900">Exa AI Search</p>
                                            <p className="text-xs text-stone-400">Connect Exa for AI-powered company & contact enrichment</p>
                                        </div>
                                    </div>
                                    {exaStatus?.valid ? (
                                        <span className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full">
                                            <CheckCircle className="w-3 h-3" /> Connected
                                        </span>
                                    ) : null}
                                </div>

                                {exaStatus?.valid ? (
                                    <div className="mt-4 pt-4 border-t border-stone-100">
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={disconnectExa}
                                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100">
                                                <X className="w-3 h-3" /> Disconnect
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="mt-4 pt-4 border-t border-stone-100 space-y-3">
                                        <div className="relative">
                                            <input
                                                type={showExaKey ? 'text' : 'password'}
                                                value={exaKey}
                                                onChange={e => setExaKey(e.target.value)}
                                                placeholder="Enter your Exa API key"
                                                className="w-full px-3 py-2 pr-10 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowExaKey(!showExaKey)}
                                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600">
                                                {showExaKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            </button>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <button
                                                onClick={validateExaKey}
                                                disabled={exaValidating}
                                                className="px-3 py-1.5 text-xs font-medium bg-stone-900 text-white rounded-lg hover:bg-stone-800 disabled:opacity-50">
                                                {exaValidating ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : null}
                                                {exaValidating ? 'Validating...' : 'Validate & Connect'}
                                            </button>
                                            <a href="https://exa.ai" target="_blank" rel="noopener noreferrer"
                                                className="text-xs text-stone-400 hover:text-stone-600 flex items-center gap-1">
                                                Don't have an account? Sign up at exa.ai <ExternalLink className="w-3 h-3" />
                                            </a>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Outlook Integration */}
                            <div className="p-4 rounded-xl border border-stone-200 hover:border-stone-300 transition-colors">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                                            <Mail className="w-5 h-5 text-blue-500" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-stone-900">Microsoft Outlook</p>
                                            <p className="text-xs text-stone-400">
                                                {outlookStatus?.connected ? outlookStatus.outlook_email : 'Connect Office 365 email'}
                                            </p>
                                        </div>
                                    </div>
                                    {outlookLoading ? (
                                        <Loader2 className="w-5 h-5 text-stone-400 animate-spin" />
                                    ) : outlookStatus?.connected ? (
                                        <span className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full">
                                            <CheckCircle className="w-3 h-3" /> Connected
                                        </span>
                                    ) : outlookStatus?.needs_reconnect ? (
                                        <button
                                            onClick={connectOutlook}
                                            disabled={connectingOutlook}
                                            className="px-3 py-1.5 text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors disabled:opacity-50">
                                            {connectingOutlook ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Reconnect'}
                                        </button>
                                    ) : (
                                        <button
                                            onClick={connectOutlook}
                                            disabled={connectingOutlook}
                                            className="px-3 py-1.5 text-xs font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 disabled:opacity-50"
                                        >
                                            {connectingOutlook ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Connect'}
                                        </button>
                                    )}
                                </div>

                                {outlookStatus?.needs_reconnect && (
                                    <div className="mt-4 pt-4 border-t border-amber-100">
                                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                            <p className="text-xs font-medium text-amber-800">Session expired for {outlookStatus.outlook_email || 'your Outlook account'}</p>
                                            <p className="text-[11px] text-amber-600 mt-1">Click Reconnect above to re-authorize your Outlook access.</p>
                                        </div>
                                    </div>
                                )}

                                {outlookStatus?.connected && (
                                    <div className="mt-4 pt-4 border-t border-stone-100">
                                        <div className="flex items-center gap-3 mb-3">
                                            <div>
                                                <p className="text-sm font-medium text-stone-900">{outlookStatus.outlook_email}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button onClick={disconnectOutlook} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100">
                                                <X className="w-3 h-3" /> Disconnect
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* CommandRoom Integration */}
                            <div className="flex items-center justify-between p-4 rounded-xl border border-stone-200 hover:border-stone-300 transition-colors opacity-60">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-violet-50 flex items-center justify-center">
                                        <Link className="w-5 h-5 text-violet-500" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-stone-900">CommandRoom.ai</p>
                                        <p className="text-xs text-stone-400">Sync account data and org charts</p>
                                    </div>
                                </div>
                                <span className="px-2.5 py-1 text-xs font-medium bg-stone-100 text-stone-500 rounded-full">
                                    Coming Soon
                                </span>
                            </div>
                        </div>
                    )}


                    {/* Security */}
                    {activeTab === 'security' && (
                        <div className="max-w-lg space-y-5">
                            <div>
                                <p className="text-sm font-medium text-stone-900 mb-1">Account Email</p>
                                <p className="text-sm text-stone-500">{user?.email}</p>
                            </div>
                            <div className="pt-4 border-t border-stone-200">
                                <p className="text-sm font-medium text-stone-900 mb-3">Active Sessions</p>
                                <div className="flex items-center justify-between p-3 rounded-lg bg-stone-50 border border-stone-200">
                                    <div>
                                        <p className="text-sm font-medium text-stone-900">Current Session</p>
                                        <p className="text-xs text-stone-400">Browser session — active now</p>
                                    </div>
                                    <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200">Active</span>
                                </div>
                            </div>
                            <div className="pt-4 border-t border-stone-200">
                                <button onClick={logout} data-testid="settings-logout-btn"
                                    className="px-4 py-2 text-sm font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors">
                                    Sign Out of All Sessions
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Voice Capture */}
                    {activeTab === 'voice' && <VoiceCaptureTab />}

                    {/* AI Assistant Visual */}
                    {activeTab === 'ai-visual' && <AIVisualSettings />}
                </div>
            </div>
        </div>
    );
}


function PlanUsageTab() {
    const { plan, planLabel, planData, tiers } = usePlan();
    const [showUpgrade, setShowUpgrade] = useState(false);

    if (!planData) return <div className="py-8 text-center text-stone-500">Loading plan information...</div>;

    const PLAN_COLORS = { free: 'bg-stone-100 text-stone-700', starter: 'bg-blue-100 text-blue-700', pro: 'bg-stone-900 text-white', enterprise: 'bg-amber-100 text-amber-700' };
    const RESOURCE_LABELS = {
        users: 'Team Members', contacts: 'Contacts', accounts: 'Companies', deals: 'Deals',
        booking_links: 'Booking Links', email_sequences: 'Email Sequences', deal_rooms: 'Deal Rooms', heartbeats: 'AI Heartbeats',
    };

    return (
        <div className="space-y-6" data-testid="plan-usage-tab">
            {/* Current Plan Banner */}
            <div className="flex items-center justify-between p-5 rounded-xl border border-stone-200 bg-stone-50">
                <div className="flex items-center gap-4">
                    <div className={`px-4 py-2 rounded-lg text-sm font-bold ${PLAN_COLORS[plan] || PLAN_COLORS.free}`}>
                        {planLabel}
                    </div>
                    <div>
                        <p className="font-semibold text-stone-900">{planData.description}</p>
                        <p className="text-sm text-stone-500">${planData.price}/month per user</p>
                    </div>
                </div>
                <Button onClick={() => setShowUpgrade(true)} variant="outline" className="text-sm" data-testid="view-plans-btn">
                    View All Plans
                </Button>
            </div>

            {/* Usage Meters */}
            <div>
                <h3 className="text-sm font-semibold text-stone-500 uppercase mb-3">Resource Usage</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {Object.entries(planData.usage || {}).map(([key, usage]) => (
                        <div key={key} className="p-4 rounded-xl border border-stone-200" data-testid={`usage-${key}`}>
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-stone-700">{RESOURCE_LABELS[key] || key}</span>
                                <span className="text-xs text-stone-500">
                                    {usage.current} / {usage.unlimited ? 'Unlimited' : usage.limit}
                                </span>
                            </div>
                            <div className="w-full h-2 bg-stone-100 rounded-full overflow-hidden">
                                <div
                                    className={`h-full rounded-full transition-all ${usage.percentage > 90 ? 'bg-red-500' : usage.percentage > 70 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                                    style={{ width: `${Math.min(usage.unlimited ? 0 : usage.percentage, 100)}%` }}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Features */}
            <div>
                <h3 className="text-sm font-semibold text-stone-500 uppercase mb-3">Feature Access</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {Object.entries(planData.features || {}).map(([key, enabled]) => (
                        <div key={key} className="flex items-center gap-2 p-2.5 rounded-lg border border-stone-100">
                            <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${enabled ? 'bg-emerald-100 text-emerald-600' : 'bg-stone-100 text-stone-400'}`}>
                                {enabled ? <CheckCircle className="w-3 h-3" /> : <X className="w-3 h-3" />}
                            </div>
                            <span className={`text-sm ${enabled ? 'text-stone-700' : 'text-stone-400'}`}>
                                {key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            <UpgradeModal open={showUpgrade} onClose={() => setShowUpgrade(false)} />
        </div>
    );
}
